import { type Observable, from, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { action$, extractPayload, ofType } from '@ardoq/rxbeach';
import { isArdoqError } from '@ardoq/common-helpers';
import { toIdSet } from '@ardoq/core';
import type {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { api, transformationsApi } from '@ardoq/api';
import {
  activeFutureStateIds$,
  type ActiveFutureStateIds,
} from './futureStatePage$';
import {
  futureStateSelectionChanged,
  type FutureStateSelectionChangedPayload,
} from './selectComponents/futureStateSelectedComponents$';
import { type ReferenceEndpoints } from './hiddenComponents';

export type LoadedFutureStateSelection = {
  status: 'loaded';
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
};

export type FutureStateSelection =
  | { status: 'loading' }
  | LoadedFutureStateSelection;

const LOADING_SELECTION: FutureStateSelection = { status: 'loading' };

const EMPTY_LOADED_SELECTION: FutureStateSelection = {
  status: 'loaded',
  components: [],
  references: [],
};

const referencesBetween = async (
  componentIds: ArdoqId[]
): Promise<APIReferenceAttributes[]> => {
  const result = await transformationsApi.referencesBetween(componentIds);
  if (isArdoqError(result)) {
    api.logErrorIfNeeded(result);
    return [];
  }
  return result;
};

const selectionFor = async (
  ids: ActiveFutureStateIds
): Promise<FutureStateSelection> => {
  const result = await transformationsApi.fetchFutureStateSelection(ids);
  if (isArdoqError(result)) {
    api.logErrorIfNeeded(result);
    return EMPTY_LOADED_SELECTION;
  }
  const { components } = result;
  if (components.length === 0) return EMPTY_LOADED_SELECTION;
  const references = await referencesBetween(components.map(({ _id }) => _id));
  return { status: 'loaded', components, references };
};

const refetchTrigger$ = (
  ids: ActiveFutureStateIds
): Observable<ActiveFutureStateIds> =>
  action$.pipe(
    ofType(futureStateSelectionChanged),
    extractPayload<FutureStateSelectionChangedPayload>(),
    filter(({ futureStateId }) => futureStateId === ids.futureStateId),
    map(() => ids),
    startWith(ids)
  );

const fetchSelection = (
  ids: ActiveFutureStateIds
): Observable<FutureStateSelection> =>
  from(selectionFor(ids)).pipe(catchError(() => of(EMPTY_LOADED_SELECTION)));

/**
 * The persisted component selection for the active future state plus the
 * references between those components, refetched on page load and after a
 * selection change is committed. A refetch keeps the previous selection until
 * the new one arrives.
 */
export const futureStateSelection$: Observable<FutureStateSelection> =
  activeFutureStateIds$.pipe(
    switchMap(ids =>
      refetchTrigger$(ids).pipe(
        switchMap(fetchSelection),
        startWith(LOADING_SELECTION)
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const toScopeComponentIds = ({
  components,
}: LoadedFutureStateSelection): Set<ArdoqId> => toIdSet(components);

export const toEndpointsByReferenceId = ({
  references,
}: LoadedFutureStateSelection): Record<ArdoqId, ReferenceEndpoints> =>
  Object.fromEntries(
    references.map(({ _id, source, target }) => [_id, { source, target }])
  );
